import { AxiosResponse } from 'axios';
import { HttpMethod } from '../utils/enums/http-method.enum';
import { isSiteCanadian } from '../utils/helpers';
import { RequestClientInterfaceService } from './request-client.interface.service';
import {
    AccountType,
    PaymentDataSearhType,
    PaymentProcessStatus,
    PaymentReason,
    PaymentSource,
    PaymentType,
} from '../utils/enums/payment.enum';
import {
    COGNITO_TOKEN_NAME,
    DEALER_PORTAL_URL,
    PERMISSIONS_TOKEN_NAME,
} from '../config';
import {
    PaymentDataResponse,
    PaymentHisotryTableData,
    PaymentInformation,
} from './models/payment.service';

/**
 * Class responsible for manage the API calls and data request of the App
 */
export class PaymentsService {
    private readonly dealerPortalUrlStructure = `${DEALER_PORTAL_URL}`;
    private client: RequestClientInterfaceService;
    private headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: '',
    };
    /**
     * Instantiates a new ClientService
     */
    constructor() {
        this.client = new RequestClientInterfaceService();
    }

    public async paymentHistory(
        dealerId: string,
    ): Promise<AxiosResponse<PaymentInformation[]>> {
        const url = `${this.dealerPortalUrlStructure}/paymentHistory`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );
        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }

    public async paymentSave(paymentSave: any): Promise<
        AxiosResponse<{
            Payment: { ReferenceNumber: string; WithdrawalDate: string };
            isSuccess: boolean;
        }>
    > {
        const url = `${this.dealerPortalUrlStructure}/paymentSave`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );
        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                jwtToken: permissionsToken,
                ...paymentSave,
            },
        };
        return this.client.sendRequest(request);
    }

    public async paymentEdit(paymentSave: any): Promise<
        AxiosResponse<{
            Payment: { ReferenceNumber: string; WithdrawalDate: string };
            isSuccess: boolean;
        }>
    > {
        const url = `${this.dealerPortalUrlStructure}/paymentEdit`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );
        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                jwtToken: permissionsToken,
                ...paymentSave,
            },
        };
        return this.client.sendRequest(request);
    }

    public async paymentDelete(payment: {
        dealerId: string;
        ApproverId: string;
        ReferenceNumber: string;
    }): Promise<
        AxiosResponse<{
            isSuccess: boolean;
        }>
    > {
        const url = `${this.dealerPortalUrlStructure}/paymentDelete`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );
        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                jwtToken: permissionsToken,
                ...payment,
            },
        };
        return this.client.sendRequest(request);
    }

    public async paymentPedings(dealerId: string): Promise<AxiosResponse<any>> {
        const url = `${this.dealerPortalUrlStructure}/paymentPendings`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );
        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                jwtToken: permissionsToken,
            },
        };
        return this.client.sendRequest(request);
    }

    public async paymentData(
        dealerId: string,
        searchType: PaymentDataSearhType,
        searchValue: string,
        paymentSource: PaymentSource,
        brand: string,
        requestingDealer: string,
        dccNumber: string,
        country?: string,
    ): Promise<AxiosResponse<PaymentDataResponse>> {
        const url = `${this.dealerPortalUrlStructure}/getPaymentData`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );
        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                searchType,
                searchValue,
                paymentSource,
                brand,
                requestingDealer,
                dccNumber,
                jwtToken: permissionsToken,
                ...(!isSiteCanadian() && { country: country || 'USA' }),
            },
        };
        return this.client.sendRequest(request);
    }

    public mapResponse(data: PaymentInformation[]): PaymentHisotryTableData[] {
        const formattedData = data.map((item, index) => {
            return {
                key: index,
                processDate: new Date(item.ProcessDateTime),
                accountType: item.AccountType as AccountType,
                paymentStatus:
                    item.PaymentProcessStatus as PaymentProcessStatus,
                account: item.AccountNumber,
                amount: item.PaymentAmount,
                paymentReason: item.SelectedPaymentReason as PaymentReason,
                comment: item.DealerComment,
                userName: item.UserID,
                paymentType: item.TransactionType as PaymentType,
            } as PaymentHisotryTableData;
        });
        return formattedData;
    }

    public abort() {
        this.client.controller.abort();
    }
}
